import {
  BoxGeometry,
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  Raycaster,
  Vector2,
  type Camera,
  type Object3D,
} from 'three'

import {MODEL_HIT_MIN_EXTENT_RATIO} from '../config'
import {clientToNdc} from '../util/viewport'

/**
 * "Did that tap land on the model?" - answered by an invisible box, never by the
 * model's own meshes.
 *
 * Raycasting the SkinnedMeshes is the obvious implementation and is wrong here
 * for a reason that is specific and checkable, not aesthetic. In r185
 * `SkinnedMesh.raycast` does:
 *
 *     if ( this.boundingSphere === null ) this.computeBoundingSphere();
 *
 * once, ever, and three never invalidates it - its own docstring says the sphere
 * "should be recomputed per frame" for an animated mesh. The pose that happens
 * to be on screen at the FIRST tap of the session is therefore frozen into the
 * hit test for the rest of it. Entrance clips typically start at a near-zero
 * scale (CatMascot's `Appear` skins the rig at 0.05 on frame 0), so one early
 * tap can cache a sphere a twentieth of the right size and make the model
 * permanently untappable. It is the same stale-bounds trap `normalize.ts`
 * documents for frustum culling, on a different code path. That entrance is now
 * 3.25 s rather than 1.25 s, so the window in which a tap could poison the cache
 * is nearly three times wider than when this was written - the argument got
 * stronger, not weaker.
 *
 * Two more, either of which would be enough on its own:
 *
 *  - `normalize.ts`'s `Box3.setFromObject` has already forced each
 *    `SkinnedMesh.boundingBox` to the BIND pose, and `raycast` rejects against
 *    it before touching geometry. A raised arm is outside that box, so rays
 *    through it miss geometry that is visibly there.
 *  - The cost. The reference model is ~26,700 triangles across 29 primitives,
 *    and `checkGeometryIntersection` re-skins three vertices per triangle with
 *    four matrix multiplies each - roughly 320,000 mat4 multiplies per tap, on a
 *    phone, uncached.
 *
 * A box also makes a better target than a silhouette would: the gap between the
 * ears, the armpits and six ~2 mm whisker tubes are all misses on an exact test.
 *
 * WHY A BOX AND NOT A CYLINDER: a cylinder sized from `groundRadius` is
 * `max(x, z) / 2` on both axes, which is right for a roughly round mascot and
 * grossly oversized for a car, a banner or anything long and thin - it would
 * swallow floor taps the user can plainly see are beside the model. The box is
 * the measurement itself, so it adapts to any silhouette, and living under the
 * pivot it inherits the authored yaw and stays aligned to the model's own axes.
 *
 * It touches the model not at all - no traverse, no material swap, no bounds
 * recompute - which is the same contract `effects/entrance-effect.ts` keeps.
 */

export type ModelHitWarning = {
  readonly kind: 'degenerateSize'
  readonly size: readonly [number, number, number]
}

export interface ModelHitTarget {
  /**
   * Parent this under `NormalizedModel.pivot` - NOT under the placement root.
   *
   * The pivot owns the normalisation scale, the y offset and the authored yaw,
   * so a child gets all three for free and `normalize.ts` stays the only file
   * that knows that maths. It also sidesteps `setContent()`'s `root.clear()`,
   * which is what forces the shadow catcher and the entrance effect to be added
   * after it: the pivot is what gets cleared INTO.
   */
  readonly proxy: Object3D
  /** Half-extents actually used, in the pivot's own units. For `?debug=1`. */
  readonly extents: readonly [number, number, number]
  /**
   * True when the ray through these CLIENT coordinates enters the volume.
   *
   * Client, not NDC: the conversion is part of the answer (see util/viewport.ts)
   * and every caller getting it right independently is not a thing that happens.
   *
   * Does NOT check whether the model is placed. `Raycaster` ignores
   * `Object3D.visible` - `intersect()` tests `layers` and nothing else - so a
   * hidden `placement-root` would still report hits. The caller owns that gate,
   * because falling through to placement is the right answer there.
   */
  test(clientX: number, clientY: number): boolean
  readonly warnings: readonly ModelHitWarning[]
  dispose(): void
}

export interface ModelHitDeps {
  readonly camera: Camera
  readonly canvas: HTMLCanvasElement
  /** `NormalizedModel.localSize` - the bind-pose AABB, in the pivot's units. */
  readonly localSize: readonly [number, number, number]
}

export const describeModelHitWarning = (warning: ModelHitWarning): string =>
  `model bounds are degenerate (${warning.size.map((v) => v.toFixed(4)).join(' x ')}); the model cannot be tapped`

export const createModelHitTarget = ({camera, canvas, localSize}: ModelHitDeps): ModelHitTarget => {
  const warnings: ModelHitWarning[] = []
  const [sx, sy, sz] = localSize

  // The reference length is the LARGEST dimension, not the height. A rug or a
  // floor decal has almost no height, and anchoring the minimum to it would
  // leave a target as flat as the model itself.
  const reference = Math.max(sx, sy, sz)

  if (!Number.isFinite(reference) || reference <= 0) {
    // Mirrors `createEntranceEffect`'s `effect: 'none'` early return and
    // `createAnimationController`'s no-clip one: a real object of the same
    // shape, so no call site has to know the difference. `test` returning false
    // means every tap falls through to placement, which is the pre-reaction
    // behaviour and the only safe answer for a model we could not measure.
    warnings.push({kind: 'degenerateSize', size: [sx, sy, sz]})
    const empty = new Mesh()
    empty.name = 'model-hit-disabled'
    empty.visible = false
    return {
      proxy: empty,
      extents: [0, 0, 0],
      test: () => false,
      warnings,
      dispose: () => empty.removeFromParent(),
    }
  }

  const minHalf = reference * MODEL_HIT_MIN_EXTENT_RATIO
  const half: readonly [number, number, number] = [
    Math.max(sx * 0.5, minHalf),
    Math.max(sy * 0.5, minHalf),
    Math.max(sz * 0.5, minHalf),
  ]

  const geometry = new BoxGeometry(half[0] * 2, half[1] * 2, half[2] * 2)
  // Never rendered except on /debug, and the raycaster only reads `position`.
  // Dropping these also makes `checkGeometryIntersection` skip its uv/normal
  // interpolation on every hit.
  geometry.deleteAttribute('normal')
  geometry.deleteAttribute('uv')

  const material = new MeshBasicMaterial({
    // `checkIntersection` honours `material.side`, and FrontSide would report
    // nothing once the camera is inside the volume - i.e. the model would become
    // untappable the moment someone walked into it. DoubleSide makes it behave
    // as a solid the whole way in. Costs nothing: never drawn.
    side: DoubleSide,
    // Only ever seen on /debug?proxy=1. Configured here rather than swapped in
    // there, so the debug page needs no knowledge of this file's internals - it
    // just flips `proxy.visible`.
    wireframe: true,
    depthTest: false,
    color: 0x00ff88,
  })

  const proxy = new Mesh(geometry, material)
  proxy.name = 'model-hit-proxy'
  // `align` in normalize.ts drops the model's lowest point to y = 0 and centres
  // it on XZ in this very space, so the measured box is exactly x/z-centred and
  // sitting on the floor. Half the height up puts the proxy on top of it.
  proxy.position.y = half[1]
  // Zero draw calls and zero shadow cost: both `WebGLRenderer.projectObject` and
  // `WebGLShadowMap.renderObject` return early on `visible === false`. The
  // raycaster does NOT - which is exactly what makes this pattern work, and is
  // the same idiom as scene/ground.ts.
  proxy.visible = false

  const ndc = new Vector2()
  const raycaster = new Raycaster()

  return {
    proxy,
    extents: half,
    warnings,

    test: (clientX, clientY) => {
      if (clientToNdc(canvas, clientX, clientY, ndc) === null) return false
      raycaster.setFromCamera(ndc, camera)
      // r185's Raycaster does not refresh world matrices. The renderer's last
      // frame normally covers it, but a tap can arrive before the first render
      // of a freshly-placed model, and walking the parents costs nothing next to
      // being wrong for one frame.
      proxy.updateWorldMatrix(true, false)
      // `false` - never recursive. The model's own SkinnedMeshes hang off a
      // sibling of this node and must never be reached; see the header.
      return raycaster.intersectObject(proxy, false).length > 0
    },

    dispose: () => {
      proxy.removeFromParent()
      geometry.dispose()
      material.dispose()
    },
  }
}
